import {
  User,
  Users,
  Code,
  Globe,
  MagnifyingGlass,
  CurrencyDollar,
  Kanban,
  FileText,
  BookOpen,
  Calendar,
  Megaphone,
  Target,
  Lightning,
  GitBranch,
  Clock,
} from '@phosphor-icons/react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PhosphorIcon = typeof MagnifyingGlass;

export type UseCaseData = {
  slug: string;
  title: string;
  headline: string;
  subtitle: string;
  icon: PhosphorIcon;
  iconColor: string;
  problemLabel: string;
  problemHeadline: string;
  problemDescription: string;
  problemPoints: string[];
  solutionHeadline: string;
  solutionDescription: string;
  benefits: { icon: PhosphorIcon; title: string; description: string }[];
  ctaHeadline: string;
  ctaSubtext: string;
  ctaButtonLabel: string;
};

export const USE_CASES: UseCaseData[] = [
  {
    slug: 'individuals',
    title: 'For Individuals & Prosumers',
    headline: 'Stop paying for 5 apps you barely use',
    subtitle:
      'Torch replaces your scattered toolkit with one workspace. Search everything, build anything, pay once.',
    icon: User,
    iconColor: '#ededed',
    problemLabel: 'The Problem',
    problemHeadline: 'Your tools are bleeding you dry',
    problemDescription:
      "You're a freelancer, solopreneur, or knowledge worker paying for Notion ($10/mo), Linear ($8/mo), Google Calendar (free but siloed), a separate notes app, and maybe a wiki tool. Your work is scattered across all of them. You search one app, don't find it, search another.",
    problemPoints: [
      'Paying $30-80/month across 4-5 separate apps',
      'Constantly switching tabs to find what you need',
      'No single search that spans all your tools',
      'Each app is a silo with its own login and learning curve',
    ],
    solutionHeadline: 'One workspace. Everything included.',
    solutionDescription:
      'Torch replaces all of them with one workspace. Search across everything. Built-in kanban, docs, wiki, calendar, and notes — all included.',
    benefits: [
      {
        icon: CurrencyDollar,
        title: 'Save $30-80/month in separate subscriptions',
        description:
          'Stop paying for Notion, Linear, Lattice, and a dozen other tools. Torch includes kanban, docs, wiki, OKRs, calendar, and notes.',
      },
      {
        icon: MagnifyingGlass,
        title: 'One search bar finds anything',
        description:
          'Search across Gmail, Slack, Drive, and more from a single search bar. No more guessing which app has the file.',
      },
      {
        icon: Kanban,
        title: 'Built-in productivity apps replace standalone tools',
        description:
          'Kanban boards, documents, wiki, calendar, notes, and OKRs — all built in, all connected.',
      },
      {
        icon: Globe,
        title: 'Works across Microsoft AND Google ecosystems',
        description:
          'Not locked into one vendor. Connect Google Workspace, Microsoft 365, Slack, and more simultaneously.',
      },
    ],
    ctaHeadline: 'Simplify your workflow',
    ctaSubtext: 'Join the waitlist and stop juggling apps.',
    ctaButtonLabel: 'Start for free',
  },
  {
    slug: 'teams',
    title: 'For Teams',
    headline: "Your team's knowledge, one search away",
    subtitle:
      'Stop losing institutional knowledge in Slack threads and scattered docs. Torch indexes everything and gives your team built-in tools to stay organized.',
    icon: Users,
    iconColor: '#6366f1',
    problemLabel: 'The Problem',
    problemHeadline: 'Knowledge is trapped in silos',
    problemDescription:
      'Your 5-20 person team uses Slack for chat, Notion for docs, Google Drive for files, Linear for tasks, and Gmail for everything else. New hires take weeks to find anything. Institutional knowledge lives in someone\'s head or buried in a Slack thread from 6 months ago.',
    problemPoints: [
      'New hires spend weeks figuring out where things live',
      'Institutional knowledge is trapped in Slack DMs and old threads',
      'No single place to search across every tool the team uses',
      'Paying $50+/user/mo across separate tools',
    ],
    solutionHeadline: 'One search. Built-in tools. Zero silos.',
    solutionDescription:
      "Torch indexes everything your team uses and makes it instantly searchable. Plus built-in project management, documents, and wiki — so you don't need separate tools.",
    benefits: [
      {
        icon: MagnifyingGlass,
        title: 'New hires find answers in seconds, not days',
        description:
          'One search bar spans every connected tool. Onboarding goes from weeks to hours.',
      },
      {
        icon: Kanban,
        title: 'Built-in kanban boards and sprints replace project trackers',
        description:
          'Manage tasks and sprints without paying for a separate project management tool.',
      },
      {
        icon: BookOpen,
        title: 'Team wiki captures institutional knowledge',
        description:
          'A shared knowledge base with nested pages, markdown, and search. Decisions are documented, not lost.',
      },
      {
        icon: CurrencyDollar,
        title: '$22/user/mo replaces $50+/user/mo in separate tools',
        description:
          'Consolidate Notion, Linear, Lattice, and more into one platform at a fraction of the cost.',
      },
    ],
    ctaHeadline: 'Unite your team',
    ctaSubtext: 'Join the waitlist and give your team a single source of truth.',
    ctaButtonLabel: 'Get started with your team',
  },
  {
    slug: 'engineering',
    title: 'For Engineering Teams',
    headline: 'Search GitHub, Jira, and Slack without switching tabs',
    subtitle:
      'Torch connects all your dev tools and makes them searchable from one place. Built-in sprint planning means one less tool to manage.',
    icon: Code,
    iconColor: '#22d3ee',
    problemLabel: 'The Problem',
    problemHeadline: 'Dev tools are everywhere',
    problemDescription:
      "Your engineering team lives in GitHub, Jira/Linear, Slack, Confluence, and Google Drive. Finding a design doc means checking 3 different tools. Sprint planning requires switching between your tracker, docs, and Slack threads. Code review discussions are split between GitHub comments, Slack threads, and Notion pages.",
    problemPoints: [
      'Design docs, RFCs, and specs scattered across 3+ tools',
      'Sprint planning requires context from multiple apps',
      'Code review conversations split between GitHub, Slack, and docs',
      'No way to cross-reference code discussions with design docs',
    ],
    solutionHeadline: 'All your dev tools. One search.',
    solutionDescription:
      'Torch connects to all your dev tools and makes them searchable from one place. Built-in sprints and kanban boards mean you can manage your engineering workflow without a separate project tracker.',
    benefits: [
      {
        icon: GitBranch,
        title: 'Search GitHub repos, PRs, issues alongside Slack and docs',
        description:
          'Find that PR discussion, the related RFC, and the Slack thread — all from one search.',
      },
      {
        icon: Lightning,
        title: 'Built-in sprint planning and kanban boards',
        description:
          'Plan sprints, track velocity, and manage your backlog without a separate tool.',
      },
      {
        icon: Target,
        title: 'OKR tracking for engineering goals',
        description:
          'Align engineering work to business objectives with built-in OKR tracking.',
      },
      {
        icon: FileText,
        title: 'Cross-reference code discussions with design docs',
        description:
          'Instantly find related conversations, specs, and decisions across all connected tools.',
      },
    ],
    ctaHeadline: 'Level up your eng team',
    ctaSubtext: 'Join the waitlist and search your entire dev stack from one place.',
    ctaButtonLabel: 'Try Torch for your eng team',
  },
  {
    slug: 'remote',
    title: 'For Remote & Distributed Teams',
    headline: "Your distributed team's brain, searchable",
    subtitle:
      "When your team is remote, knowledge fragmentation is 10x worse. Torch becomes your team's searchable memory.",
    icon: Globe,
    iconColor: '#f59e0b',
    problemLabel: 'The Problem',
    problemHeadline: 'Remote makes everything harder to find',
    problemDescription:
      "When your team is remote, knowledge fragmentation is 10x worse. Decisions get made in Slack DMs that nobody else sees. Meeting notes live in one person's Google Doc. The onboarding doc is outdated because it's in Notion and nobody remembers to update it. Time zones mean you can't just tap someone on the shoulder and ask.",
    problemPoints: [
      'Decisions made in Slack DMs that nobody else sees',
      "Meeting notes trapped in one person's Google Doc",
      'Onboarding docs outdated because they live in a separate tool',
      "Can't tap someone on the shoulder — need searchable answers",
    ],
    solutionHeadline: "Your team's searchable memory.",
    solutionDescription:
      "Torch becomes your team's searchable memory. Every tool, every conversation, every document — indexed and findable. Built-in announcements keep everyone aligned. Wiki captures decisions so they're not lost in Slack threads.",
    benefits: [
      {
        icon: MagnifyingGlass,
        title: 'Async-first: search replaces "hey, where\'s that doc?"',
        description:
          'Find anything without waiting for someone in another time zone to wake up and answer.',
      },
      {
        icon: Megaphone,
        title: 'Announcements keep distributed teams aligned',
        description:
          'Company-wide updates with read receipts. No more "did everyone see that Slack message?"',
      },
      {
        icon: BookOpen,
        title: 'Wiki becomes your single source of truth',
        description:
          "Capture decisions, processes, and context in a searchable wiki. No more outdated Notion pages.",
      },
      {
        icon: Calendar,
        title: 'Calendar integration works across time zones',
        description:
          'See your team\'s availability across time zones in one unified calendar.',
      },
      {
        icon: Clock,
        title: 'Everything is searchable — even past Slack conversations',
        description:
          'Historical conversations, documents, and decisions are all indexed and findable.',
      },
    ],
    ctaHeadline: 'Unite your remote team',
    ctaSubtext: 'Join the waitlist and give your distributed team a shared brain.',
    ctaButtonLabel: 'Unite your remote team',
  },
];

export function getUseCaseBySlug(slug: string): UseCaseData | undefined {
  return USE_CASES.find((uc) => uc.slug === slug);
}
